<template>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="{ fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <AppLayoutNavigation v-if="!isMobile" class="p-4" />
      <main class="p-4 pt-0">
        <article>
          <RouterView />
        </article>
      </main>
    </template>
  </VaLayout>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'

import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'
import { useStaffStore } from '../stores/staff'
import { useDepartmentStore } from '../stores/department'
import { useCategoryStore } from '../stores/category'
import { useCarInfoStore } from '../stores/car-info'
import { useInvoiceListStore } from '../stores/invoice'
import { useContractListStore } from '../stores/contract'
import { useInteriorListStore } from '../stores/interior'
import { useFuelFormListStore } from '../stores/fuel-form'
import { useSalesPlanListStore } from '../stores/sales-plan'
import { useQuotationListStore } from '../stores/quotation'
import { useInspectionListStore } from '../stores/inspection'
import { useLiquidationListStore } from '../stores/liquidation'
import { useSellContractListStore } from '../stores/sell-contract'
import { useSalesQuarterListStore } from '../stores/sales-quarter'
import { useAccessaryFormStore } from '../stores/accessary-form'
import { useAccessoryFormStore } from '../stores/accessory-form'
import { useRepairCommandListStore } from '../stores/repair-command'
import { useSalesKpiMonthListStore } from '../stores/sales-kpi-month'
import { useInvoiceRequestListStore } from '../stores/invoice-request'
import { usePaymentRequestListStore } from '../stores/payment-request'
import { useAdvanceRequestStore } from '../stores/advance-request'
import { useHandoverRequestListStore } from '../stores/handover-request'
import { useRetrievalRequestListStore } from '../stores/retrieval-request'
import { useRepairSuggestionListStore } from '../stores/repair-suggestion'
import { useSellRequestSourceStore } from '../stores/sell-request-source'
import { useCardListStore } from '../stores/card'

import moment from 'moment'

const GlobalStore = useGlobalStore()
const departmentStore = useDepartmentStore()
const categoryStore = useCategoryStore()
const carInfoStore = useCarInfoStore()
const invoiceStore = useInvoiceListStore()
const contractStore = useContractListStore()
const interiorStore = useInteriorListStore()
const fuelFormStore = useFuelFormListStore()
const salesPlanStore = useSalesPlanListStore()
const quotationStore = useQuotationListStore()
const inspectionStore = useInspectionListStore()
const liquidationStore = useLiquidationListStore()
const sellContractStore = useSellContractListStore()
const salesQuarterStore = useSalesQuarterListStore()
const accessaryFormStore = useAccessaryFormStore()
const accessoryFormStore = useAccessoryFormStore()
const repairCommandStore = useRepairCommandListStore()
const salesKpiMonthStore = useSalesKpiMonthListStore()
const invoiceRequestStore = useInvoiceRequestListStore()
const paymentRequestStore = usePaymentRequestListStore()
const advanceRequestStore = useAdvanceRequestStore()
const handoverRequestStore = useHandoverRequestListStore()
const retrievalRequestStore = useRetrievalRequestListStore()
const repairSuggestionStore = useRepairSuggestionListStore()
const sellRequestSourceStore = useSellRequestSourceStore()
const cardStore = useCardListStore()
const staffStore = useStaffStore()
const breakpoints = useBreakpoint()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)

const isMobile = ref(false)
const isTablet = ref(false)
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const onResize = () => {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  staffStore.listStaffs()
  departmentStore.listDepartments()
  categoryStore.listCategory()
  categoryStore.listArrangedCategory()
  carInfoStore.getCount()
  invoiceStore.countInvoices()
  contractStore.countContracts()
  interiorStore.countInteriors()
  fuelFormStore.countFuelForms()
  salesPlanStore.countSalesPlans()
  quotationStore.countQuotations()
  inspectionStore.countInspections()
  liquidationStore.countLiquidations()
  sellContractStore.countSellContracts()
  salesQuarterStore.countSalesQuarters()
  accessaryFormStore.countAccessaryForms()
  accessoryFormStore.countAccessoryForms()
  repairCommandStore.countRepairCommands()
  salesKpiMonthStore.countSalesKpiMonths()
  invoiceRequestStore.countInvoiceRequests()
  paymentRequestStore.countPaymentRequests()
  advanceRequestStore.countAdvanceRequests()
  handoverRequestStore.countHandoverRequests()
  retrievalRequestStore.countRetrievalRequests()
  repairSuggestionStore.countRepairSuggestions()
  sellRequestSourceStore.listSellRequestSources()
  cardStore.countMyCards({
    scheduled_from: moment().utcOffset(0).startOf('day').valueOf(),
    scheduled_to: moment().utcOffset(0).endOf('day').valueOf(),
  })
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}
</style>
